import React, { useState } from "react";
import { Box, Typography, Button, Link } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styles } from "./styles";
import { DOCUMENTATION_URL } from "../../constants";
import { getStarterPrompts } from "../../hooks/useStarterPrompts";
import { Prompt } from "../../types/frontend";

/**
 * Props for the StarterPage component.
 *
 * @property {function} onPromptSend - Callback function to handle sending a prompt.
 * @property {string} [title] - Optional title to display at the top of the StarterPage.
 * @property {string} [subtitle] - Optional subtitle to display below the title.
 * @property {Prompt[]} [starterPrompts] - Optional array of starter prompts for the user.
 */
interface StarterPageProps {
  onPromptSend: (text: string) => void;
  title?: string;
  subtitle?: string;
  starterPrompts?: Prompt[];
  documentationUrl?: string;
}

/**
 * StarterPage component - displays a welcome panel with title, subtitle, documentation link,
 * and a list of starter prompts that users can click to initiate a conversation.
 *
 * @param {StarterPageProps} props - The props for the component.
 * @returns {JSX.Element} The rendered StarterPage component.
 */
const StarterPage: React.FC<StarterPageProps> = ({
  onPromptSend,
  title = "Materiom AI",
  subtitle = "Your Research Assistant for Bio-Based Materials.",
  starterPrompts,
  documentationUrl = DOCUMENTATION_URL,
}) => {
  // Use lazy initialization to only compute prompts once on mount
  // This prevents random prompts from being regenerated on every parent re-render
  const [displayPrompts] = useState<Prompt[]>(
    () => starterPrompts ?? getStarterPrompts()
  );
  const theme = useTheme();

  return (
    <Box sx={styles.starterPromptsContainer}>
      <Box sx={styles.starterTitleContainer}>
        <Box
          component="img"
          src="/materiom_logo.png"
          alt="Materiom Logo"
          sx={styles.starterLogo}
        />
        <Typography
          variant="h5"
          sx={
            typeof styles.starterTitle === "function"
              ? styles.starterTitle(theme)
              : styles.starterTitle
          }
        >
          {title}
        </Typography>
      </Box>
      <Typography
        variant="subtitle1"
        sx={
          typeof styles.starterSubtitle === "function"
            ? styles.starterSubtitle(theme)
            : styles.starterSubtitle
        }
      >
        {subtitle}
      </Typography>
      <Link
        href={documentationUrl}
        target="_blank"
        rel="noopener"
        sx={styles.starterFAQLink}
        data-attr="learn-more-link"
      >
        Click here to learn more →
      </Link>
      <Typography
        sx={
          typeof styles.starterPromptsTitle === "function"
            ? styles.starterPromptsTitle(theme)
            : styles.starterPromptsTitle
        }
      >
        Try asking:
      </Typography>
      {displayPrompts.map((prompt, index) => (
        <Button
          key={index}
          variant="text"
          onClick={() => onPromptSend(prompt.message)}
          sx={
            typeof styles.starterPromptButton === "function"
              ? styles.starterPromptButton(theme)
              : styles.starterPromptButton
          }
          disableElevation
          disableRipple
          data-attr={`starter-prompt-${prompt.category}`}
        >
          {prompt.label}
        </Button>
      ))}
    </Box>
  );
};

export default StarterPage;
